import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Organization, OrganizationUpdateInput } from '../../services/organizationService';

interface OrganizationState {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  organization: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrganization = createAsyncThunk(
  'organization/fetchOrganization',
  async (orgId: number) => {
    const response = await import('../../services/organizationService').then(s => 
      s.default.getOrganization(orgId)
    );
    return response;
  }
);

export const updateOrganization = createAsyncThunk(
  'organization/updateOrganization',
  async ({ orgId, data }: { orgId: number; data: OrganizationUpdateInput }) => {
    const response = await import('../../services/organizationService').then(s => 
      s.default.updateOrganization(orgId, data)
    );
    return response;
  }
);

export const uploadOrganizationLogo = createAsyncThunk(
  'organization/uploadLogo',
  async ({ orgId, file }: { orgId: number; file: File }) => {
    const response = await import('../../services/organizationService').then(s => 
      s.default.uploadLogo(orgId, file)
    );
    return response;
  }
);

export const deleteOrganizationLogo = createAsyncThunk(
  'organization/deleteLogo',
  async (orgId: number) => {
    await import('../../services/organizationService').then(s => 
      s.default.deleteLogo(orgId)
    );
    return orgId;
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organization
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch organization';
      })
      // Update organization
      .addCase(updateOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update organization';
      })
      // Upload logo
      .addCase(uploadOrganizationLogo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadOrganizationLogo.fulfilled, (state, action: PayloadAction<Organization>) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(uploadOrganizationLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to upload logo';
      })
      // Delete logo
      .addCase(deleteOrganizationLogo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrganizationLogo.fulfilled, (state) => {
        state.loading = false;
        if (state.organization) {
          state.organization.logo_url = undefined;
        }
      })
      .addCase(deleteOrganizationLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete logo';
      });
  },
});

export const { clearError } = organizationSlice.actions;
export default organizationSlice.reducer;
